"use client";

import { useEffect } from "react";
import HeroSection from "./components/ArtDecoHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";

const neighborhood = "Miami Beach";
const zips = ["33139", "33140", "33141"];

export default function MiamiBeachPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const miamiBeachFaqs = [
    {
      question: t("hood.faq1Q"),
      answer: t("hood.faq1A"),
    },
    {
      question: t("hood.faq2Q"),
      answer: t("hood.faq2A"),
    },
    {
      question: t("hood.faq3Q"),
      answer: t("hood.faq3A"),
    },
    {
      question: t("hood.faq4Q"),
      answer: t("hood.faq4A"),
    },
    {
      question: t("miamiBeach.faq5Q"),
      answer: t("miamiBeach.faq5A"),
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparación de Ventanas Miami Beach — Calidad Marina"
      : "Miami Beach Window Repair | Salt Air Hardware Specialists";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Especialistas en reparación de ventanas en Miami Beach. Herrajes calidad marina para exposición de aire salino oceánico. Planes de mantenimiento semestrales disponibles."
      : "Miami Beach window repair specialists. Marine-grade hardware for oceanfront salt air exposure. Twice-yearly maintenance plans available.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Reparación de Ventanas Miami Beach — Calidad Marina"
        : "Miami Beach Window Repair | Salt Air Hardware Specialists",
      description: currentLang === "es"
        ? "Especialistas en reparación de ventanas en Miami Beach. Herrajes calidad marina para exposición de aire salino oceánico. Planes de mantenimiento semestrales disponibles."
        : "Miami Beach window repair specialists. Marine-grade hardware for oceanfront salt air exposure. Twice-yearly maintenance plans available.",
      url: "https://impact-window-repair-miami.com/impact-windows-miami-beach/",
      image: "https://impact-window-repair-miami.com/og/og-miami-beach.jpg",
      imageAlt: currentLang === "es"
        ? "Reparación de ventanas Miami Beach"
        : "Window repair Miami Beach",
      breadcrumbs: [
        { name: "Home", item: "https://impact-window-repair-miami.com/" },
        { name: "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: "Miami Beach", item: "https://impact-window-repair-miami.com/impact-windows-miami-beach/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: currentLang === "es" ? "Reparación de Ventanas" : "Window Repair",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
              "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
              "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: { "@type": "City", name: "Miami Beach, Miami-Dade, FL" },
          serviceType: "Window Repair",
          description: currentLang === "es"
            ? "Reparación de herrajes, vidrios y mantenimiento en Miami Beach."
            : "Hardware repair, glass replacement, and maintenance in Miami Beach.",
        },
        {
          "@type": "LocalBusiness",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
            "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
            "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "main-schema";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      mainEntity: miamiBeachFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      const s = document.getElementById("main-schema");
      if (s) document.head.removeChild(s);
      const fs = document.getElementById("faq-schema");
      if (fs) document.head.removeChild(fs);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection h1={t("miamiBeach.h1")} subhead={t("miamiBeach.subhead")} zips={zips} />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACIÓN DE VENTANAS MIAMI BEACH" : "MIAMI BEACH WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/323a28f6-84cb-4364-a26a-dc0d16f18b90_Salt-Air-Window-Repair-Specialists-in-Miami-Beach.jpg?v=c3ca64e28dfe88b2c8013670ba124e32"
        imageAlt={currentLang === "es"
          ? "Especialista en reparación de ventanas Miami Beach reemplazando herrajes en propiedad frente al océano"
          : "Miami Beach window repair specialist replacing hardware on oceanfront property"}
        heading={currentLang === "es"
          ? "Especialistas en Reparación de Ventanas Aire Salino en Miami Beach"
          : "Salt Air Window Repair Specialists in Miami Beach"}
        lead={t("miamiBeach.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reemplazos de herrajes de acero inoxidable y calidad marina",
                "Reemplazo de burletes para exposición oceánica",
                "Servicio de rodillos y cilindros de puertas corredizas",
              ]
            : [
                "Stainless and marine-grade hardware replacements",
                "Weatherstripping replacement for oceanfront exposure",
                "Sliding door rollers and lock cylinder service",
              ]
        }
        closing={currentLang === "es"
          ? "Las propiedades de Miami Beach se benefician de partes especializadas para costa y planes de mantenimiento semestrales."
          : "Miami Beach properties benefit from coastal-specialized parts and twice-yearly maintenance plans."}
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection items={miamiBeachFaqs} heading={t("hood.commonFaqH2")} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: "Bay Harbor Islands", slug: "bay-harbor-islands" },
        { name: "North Miami Beach", slug: "north-miami-beach" },
        { name: "Aventura", slug: "aventura" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}